import React, { ReactNode } from 'react'
import Head from 'next/head'
import { Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CartType, productDetailRoute, useRouter } from '@src/routes'
import { Link } from '@src/components/Link'
import { sendCarouselItemClickEvent } from '@src/utils/analytics/carouselAnalytics'
import { useCategorizePage } from '@src/utils/analytics/CategorizePage'
import { FormattedMessage } from 'react-intl'
import {
  CarouselProductTypes,
  CarouselProduct,
} from './DynamicProductCardTypes'
import { ProductLockupMini } from '@src/components/ProductLockupMini/ProductLockupMini'
import AddToCartButton, {
  Variants as AddToCartVariants,
  Colors as AddToCartColors,
} from '@src/components/AddToCartButton'
import { sendbuyItNowCarouselAddToCartEvent } from '@src/utils/analytics/cartAndCheckoutEvents'

const useStyles = makeStyles()((theme) => ({
  link: {
    border: '2px solid transparent',
    display: 'block',
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.primary,
    // match CTA button focus styles
    '&:focus-visible': {
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: `0 0 6px 0 ${theme.palette.primary.main}`,
      outline: 'none',
    },
  },
  footer: {
    marginTop: theme.spacing(6),
  },
  footerRaisedCard: {
    marginTop: theme.spacing(4),
  },
  btn: {
    whiteSpace: 'nowrap',
  },
}))

interface DataAttributes {
  'data-item_id'?: string
  'data-item_brand'?: string
  'data-item_list_id'?: string
  'data-item-list_name'?: string
  'data-gaproductcode'?: string
}

interface DynamicProductCardProps {
  className?: string
  displayDiscount?: (discountPercent?: string | null) => ReactNode
  hideProductImg?: boolean
  index: number
  isRaisedCardLayout?: boolean
  onLinkClick?: () => void
  preloadCardImages?: boolean
  product: CarouselProduct
  testId: string
  type: CarouselProductTypes
  additionalInfoRenderer?: (product: CarouselProduct) => ReactNode
  cardFooterVariant?: 'quick-view' | 'none' | 'add-to-cart'
  onFooterAction?: (product: CarouselProduct) => Promise<void> | void
  analyticsItemListingId?: string | null
  analyticsItemListingName?: string | null
  dataAttributes?: DataAttributes
}

const DynamicProductCard: React.FC<DynamicProductCardProps> = ({
  product,
  testId,
  onLinkClick,
  preloadCardImages = false,
  className,
  isRaisedCardLayout,
  index,
  hideProductImg = false,
  displayDiscount,
  additionalInfoRenderer,
  cardFooterVariant = 'quick-view',
  onFooterAction,
  analyticsItemListingId,
  analyticsItemListingName,
  dataAttributes, //Sets data-attributes on carousel html elements in the browser for analytics use as part of UIPT-979 POC work
  type,
}) => {
  const router = useRouter()
  const catalog = router?.query?.catalog || ''
  const { classes, cx } = useStyles()
  const linkId = `product-card-${product.productNumber}${product.brand.key}-${testId}`

  const pageType = useCategorizePage()
  const analyticsCreativeSlot = `${type} - ${pageType}`

  const isMarketplace = router?.query?.type === CartType.MarketplaceCart

  return (
    <div
      className={cx(className, 'promotions-slider-div')}
      data-index={index + 1}
      {...dataAttributes}
    >
      {preloadCardImages && (
        <Head>
          <link rel="preload" href={product.images[0].mediumUrl} as="image" />
        </Head>
      )}
      <Link
        {...productDetailRoute.index(product.brand.key, product.productKey, {
          catalog,
        })}
        passHref
      >
        {/* jsx-a11y rules disabled because href is implicitly passed to anchor via Link component */}
        <a
          className={classes.link}
          data-testid={linkId}
          id={linkId}
          onClick={() => {
            if (onLinkClick) onLinkClick()
            sendCarouselItemClickEvent({
              creativeSlot: analyticsCreativeSlot,
              product,
              indexPosition: index,
              carouselType: type,
              promoItemId: analyticsItemListingId,
              promoItemName: analyticsItemListingName,
            })
          }}
        >
          <ProductLockupMini
            productNumber={product.productNumber}
            brand={product.brand}
            name={product.name}
            description={product.description}
            image={!hideProductImg ? product.images[0] : undefined}
            isRaisedCardLayout={isRaisedCardLayout}
            displayDiscount={displayDiscount}
            discount={product.discount}
            additionalInfo={additionalInfoRenderer?.(product) || undefined}
          />
        </a>
      </Link>

      {cardFooterVariant !== 'none' && (
        <div
          className={cx(
            isRaisedCardLayout ? classes.footerRaisedCard : classes.footer
          )}
        >
          {cardFooterVariant === 'quick-view' && (
            <Button
              className={classes.btn}
              variant="outlined"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                onFooterAction?.(product)
                sendCarouselItemClickEvent({
                  creativeSlot: analyticsCreativeSlot,
                  product,
                  indexPosition: index,
                  carouselType: type,
                  promoItemId: analyticsItemListingId,
                  promoItemName: analyticsItemListingName,
                })
              }}
            >
              <FormattedMessage id="QUICK_VIEW" defaultMessage="Quick View" />
            </Button>
          )}
          {cardFooterVariant === 'add-to-cart' && (
            <AddToCartButton
              dirty
              isSubmitting={false}
              canAddToCart
              hideAddToCartForPrepackItems={false}
              handleAddToCart={() => {
                onFooterAction?.(product)
                sendbuyItNowCarouselAddToCartEvent(
                  product,
                  CarouselProductTypes.BuyAgain,
                  isMarketplace
                )
              }}
              variant={AddToCartVariants.Contained}
              color={AddToCartColors.Secondary}
              fullWidth
              size="large"
            />
          )}
        </div>
      )}
    </div>
  )
}

export default DynamicProductCard
